import { Organization, WebSite, WithContext } from "schema-dts";

import { homepageUrl } from "@/mk2/apps/home/urls";
import {
    getAllDescendantsOfTypes,
    getAttributesDict,
    removeQuotes,
} from "@/mk2/helpers/article_ast/utils";
import { fbMorykonikProfileUrl } from "@/mk2/helpers/urls";
import { AST, PhotoEntity } from "@/mk2/schemas";
import { SERVER_FULL_NAME } from "@/mk/autogenerated/translation";
import assetUrl from "@/mk/common/assetUrl";
import { photoUrl } from "@/mk/photo/photoUrl";
import { ACTIVE_SERVER_ID, BASE_URL } from "@/mk/settings";

const instagramUrl = (): string =>
    ({
        201: "https://www.instagram.com/modrykonik_sk/",
        202: "https://www.instagram.com/modrykonik_cz/",
    })[ACTIVE_SERVER_ID];

export const organizationModrykonikWithoutContext = (full: boolean = true): Organization =>
    full
        ? {
              // according to https://developers.google.com/search/docs/data-types/article#amp
              "@type": "Organization",
              "@id": BASE_URL + "/#organization",
              url: BASE_URL + homepageUrl,
              name: SERVER_FULL_NAME,
              logo: {
                  "@type": "ImageObject",
                  url: assetUrl + `img/logos/ver-4/logo-organization-${ACTIVE_SERVER_ID}@1x.png`,
                  height: "60",
                  width: { 201: "269", 202: "269" }[ACTIVE_SERVER_ID],
              },
              sameAs: [
                  "https://www.wikidata.org/wiki/Q138546222",
                  fbMorykonikProfileUrl(),
                  instagramUrl(),
                  "https://www.youtube.com/c/Modrykonik",
              ],
          }
        : {
              "@type": "Organization",
              "@id": BASE_URL + "/#organization",
          };

export const organizationModrykonik = (full: boolean = true): WithContext<Organization> => {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        ...(organizationModrykonikWithoutContext(full) as {}),
    };
};

export const websiteModrykonik = (): WithContext<WebSite> => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": BASE_URL + "/#website",
    url: BASE_URL + homepageUrl,
    name: SERVER_FULL_NAME,
    publisher: { "@id": BASE_URL + "/#organization" },
});

export const findCoverPhotoEntityFromAst = (ast: AST, photos: PhotoEntity[]) => {
    const mediaNodes = getAllDescendantsOfTypes(ast, ["pic"]);
    for (const mediaNode of mediaNodes) {
        const attributes = getAttributesDict(mediaNode);
        if (attributes.hasOwnProperty("cover")) {
            const coverPhotoCode = removeQuotes(
                attributes.hasOwnProperty("code")
                    ? attributes.code
                    : attributes.hasOwnProperty("cd")
                      ? attributes.cd
                      : null,
            );
            return photos?.find((p) => p.code === coverPhotoCode);
        }
    }
    return null;
};

export function getCoverOrFirstPhoto(ast: AST, photos: PhotoEntity[]): PhotoEntity | null {
    if (!photos || photos.length === 0) {
        return null;
    }
    const coverPhoto: PhotoEntity = findCoverPhotoEntityFromAst(ast, photos);
    if (coverPhoto) {
        return coverPhoto;
    }
    return photos[0];
}

export function getUrlOfCoverImageOrFirstImageOrFallback(ast: AST, photos: PhotoEntity[]): string {
    const coverPhoto: PhotoEntity = findCoverPhotoEntityFromAst(ast, photos);
    return coverPhoto ? photoUrl(coverPhoto, "s1600x1600") : getUrlOfFirstImageOrFallback(photos);
}

export function getUrlOfFirstImageOrFallback(photos: PhotoEntity | PhotoEntity[]): string {
    const mainPhoto: PhotoEntity = Array.isArray(photos)
        ? photos.length > 0
            ? photos[0]
            : undefined
        : photos;

    // according to https://developers.google.com/search/docs/data-types/article#amp
    //
    // Images should be at least 1200 pixels wide.
    return mainPhoto
        ? photoUrl(mainPhoto, "s1600x1600")
        : assetUrl + "img/general/article-main-img-fallback@1x.png";
}

export function getPhotoResizedDimensions(
    photo: PhotoEntity,
    maxSize: number = 1600,
): { width: number; height: number } | null {
    if (!photo || !photo.width || !photo.height) {
        return null;
    }

    const { width, height } = photo;

    // If the image is smaller than maxSize, return original dimensions
    if (width <= maxSize && height <= maxSize) {
        return { width, height };
    }

    // Calculate ratio and resize the larger side to maxSize
    const ratio = Math.min(maxSize / width, maxSize / height);
    return {
        width: Math.round(width * ratio),
        height: Math.round(height * ratio),
    };
}
